// Note: Twilio is only used on the server (API routes)
// This client-side service only handles UI state and Supabase queries
import { supabase } from './supabase';
import { getAuthHeaders } from './api';

/**
 * Twilio Service for SMS with Local Area Code Matching
 * 
 * This service automatically selects the best local Twilio number
 * based on the client's area code to improve engagement rates.
 */

class TwilioService {
  constructor() {
    // Twilio is only used server-side in API routes
    // This client-side service handles UI and database operations
  }

  /**
   * Extract area code from phone number
   * Handles formats: +1XXXXXXXXXX, (XXX) XXX-XXXX, XXX-XXX-XXXX
   */
  extractAreaCode(phoneNumber) {
    const digits = phoneNumber.replace(/[^0-9]/g, '');
    // For US numbers, area code is the first 3 digits after country code
    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.substring(1, 4);
    } else if (digits.length === 10) {
      return digits.substring(0, 3);
    }
    return null;
  }

  /**
   * Get the best local Twilio number for a client
   * Uses database function for area code matching
   */
  async getLocalTwilioNumber(clientPhone) {
    try {
      const { data, error } = await supabase
        .rpc('get_local_twilio_number', { client_phone_number: clientPhone });

      if (error) {
        console.error('Error getting local Twilio number:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Failed to get local Twilio number:', err);
      return null;
    }
  }

  /**
   * Get or create a conversation for a client
   */
  async getOrCreateConversation(clientId, agentId, clientPhone) {
    try {
      // Check if conversation already exists
      const { data: existingConv, error: searchError } = await supabase
        .from('conversations')
        .select('*')
        .eq('client_id', clientId)
        .eq('agent_id', agentId)
        .eq('channel', 'sms')
        .single();

      if (existingConv) {
        return { data: existingConv, error: null };
      }

      // Get the best local Twilio number
      const twilioNumber = await this.getLocalTwilioNumber(clientPhone);
      
      if (!twilioNumber) {
        return { 
          data: null, 
          error: new Error('No active Twilio phone number available') 
        };
      }

      // Create new conversation
      const { data: newConv, error: createError } = await supabase
        .from('conversations')
        .insert({
          client_id: clientId,
          agent_id: agentId,
          channel: 'sms',
          client_phone: clientPhone,
          twilio_phone: twilioNumber
        })
        .select()
        .single();

      return { data: newConv, error: createError };
    } catch (err) {
      console.error('Error in getOrCreateConversation:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Send SMS using Twilio API via serverless function
   */
  async sendSMS({ conversationId, clientId, agentId, toPhone, message }) {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch('/api/sms/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          conversationId,
          clientId,
          agentId,
          toPhone,
          message
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send SMS');
      }

      const result = await response.json();
      return result;

    } catch (err) {
      console.error('Error sending SMS:', err);
      return { 
        success: false, 
        error: err.message 
      };
    }
  }

  /**
   * Get all messages for a conversation
   */
  async getMessages(conversationId) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          client:clients(name, email, phone, avatar_url)
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return { data, error: null };
    } catch (err) {
      console.error('Error fetching messages:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Get all conversations for an agent
   */
  async getConversations(agentId) {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          client:clients(name, email, phone, avatar_url, lifecycle_stage),
          latest_message:messages(content, created_at, direction, status)
        `)
        .eq('agent_id', agentId)
        .eq('is_archived', false)
        .order('last_message_at', { ascending: false })
        .order('created_at', { foreignTable: 'latest_message', ascending: false })
        .limit(1, { foreignTable: 'latest_message' });

      if (error) throw error;

      return { data, error: null };
    } catch (err) {
      console.error('Error fetching conversations:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Mark conversation as read
   */
  async markAsRead(conversationId) {
    try {
      const { error } = await supabase
        .rpc('mark_conversation_as_read', { conversation_uuid: conversationId });

      if (error) throw error;

      return { success: true, error: null };
    } catch (err) {
      console.error('Error marking conversation as read:', err);
      return { success: false, error: err };
    }
  }

  /**
   * Handle incoming SMS webhook from Twilio
   */
  async handleIncomingMessage({ 
    MessageSid, 
    From, 
    To, 
    Body, 
    MessageStatus 
  }) {
    try {
      // Find the conversation by phone numbers
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('*, client:clients(id, agent_id)')
        .eq('client_phone', From)
        .eq('twilio_phone', To)
        .single();

      if (convError || !conversation) {
        console.error('Conversation not found for incoming message:', From, To);
        return { success: false, error: 'Conversation not found' };
      }

      // Save incoming message
      const { data: message, error: msgError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          client_id: conversation.client_id,
          agent_id: conversation.agent_id,
          direction: 'inbound',
          channel: 'sms',
          content: Body,
          status: 'delivered',
          twilio_sid: MessageSid,
          twilio_status: MessageStatus,
          from_phone: From,
          to_phone: To,
          delivered_at: new Date().toISOString()
        })
        .select()
        .single();

      if (msgError) {
        console.error('Error saving incoming message:', msgError);
        return { success: false, error: msgError };
      }

      return { success: true, data: message };
    } catch (err) {
      console.error('Error handling incoming message:', err);
      return { success: false, error: err };
    }
  }

  /**
   * Update message status from Twilio webhook
   */
  async updateMessageStatus(twilioSid, status) {
    try {
      const updates = {
        twilio_status: status,
        updated_at: new Date().toISOString()
      };

      // Map Twilio status to our status enum
      const statusMap = {
        'queued': 'queued',
        'sending': 'sent',
        'sent': 'sent',
        'delivered': 'delivered',
        'undelivered': 'failed',
        'failed': 'failed',
        'read': 'read'
      };

      updates.status = statusMap[status] || status;

      if (status === 'delivered') {
        updates.delivered_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('messages')
        .update(updates)
        .eq('twilio_sid', twilioSid);

      if (error) throw error;

      return { success: true };
    } catch (err) {
      console.error('Error updating message status:', err);
      return { success: false, error: err };
    }
  }

  /**
   * Get dashboard statistics for an agent
   */
  async getDashboardStats(agentId, dateRange = '30d') {
    try {
      // Calculate date range
      const now = new Date();
      let startDate = new Date();
      
      switch (dateRange) {
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        case 'all':
          startDate = new Date(0); // Beginning of time
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }

      // Get message counts
      const { data: messages, error: msgError } = await supabase
        .from('messages')
        .select('direction, status, created_at')
        .eq('agent_id', agentId)
        .gte('created_at', startDate.toISOString());

      if (msgError) throw msgError;

      // Calculate statistics
      const sends = messages?.filter(m => m.direction === 'outbound')?.length || 0;
      const replies = messages?.filter(m => m.direction === 'inbound')?.length || 0;
      const total = messages?.length || 0;
      const replyRate = sends > 0 ? ((replies / sends) * 100).toFixed(1) : '0.0';

      // Get previous period for comparison
      const periodDays = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 30;
      const previousStartDate = new Date(startDate);
      previousStartDate.setDate(previousStartDate.getDate() - periodDays);

      const { data: previousMessages, error: prevError } = await supabase
        .from('messages')
        .select('direction')
        .eq('agent_id', agentId)
        .gte('created_at', previousStartDate.toISOString())
        .lt('created_at', startDate.toISOString());

      if (prevError) {
        console.error('Error fetching previous period stats:', prevError);
      }

      const previousSends = previousMessages?.filter(m => m.direction === 'outbound')?.length || 0;
      const previousReplies = previousMessages?.filter(m => m.direction === 'inbound')?.length || 0;
      const previousTotal = previousMessages?.length || 0;
      const previousReplyRate = previousSends > 0 ? ((previousReplies / previousSends) * 100).toFixed(1) : '0.0';

      // Calculate percentage changes
      const calculateChange = (current, previous) => {
        if (previous === 0) return current > 0 ? '+100%' : '0%';
        const change = ((current - previous) / previous) * 100;
        return change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
      };

      return {
        data: {
          sends: {
            value: sends.toLocaleString(),
            change: calculateChange(sends, previousSends),
            changeType: sends >= previousSends ? 'positive' : 'negative'
          },
          replies: {
            value: replies.toLocaleString(),
            change: calculateChange(replies, previousReplies),
            changeType: replies >= previousReplies ? 'positive' : 'negative'
          },
          replyRate: {
            value: `${replyRate}%`,
            change: calculateChange(parseFloat(replyRate), parseFloat(previousReplyRate)),
            changeType: parseFloat(replyRate) >= parseFloat(previousReplyRate) ? 'positive' : 'negative'
          },
          total: {
            value: total.toLocaleString(),
            change: calculateChange(total, previousTotal),
            changeType: total >= previousTotal ? 'positive' : 'negative'
          }
        },
        error: null
      };
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Get performance trends data (weekly breakdown)
   */
  async getPerformanceTrends(agentId, dateRange = '30d') {
    try {
      const now = new Date();
      let startDate = new Date();
      
      switch (dateRange) {
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        case 'all':
          startDate = new Date(0);
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }

      const { data: messages, error } = await supabase
        .from('messages')
        .select('direction, status, created_at')
        .eq('agent_id', agentId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group by week
      const weeklyData = {};
      messages?.forEach(msg => {
        const date = new Date(msg.created_at);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        weekStart.setHours(0, 0, 0, 0);
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            period: `Week ${Object.keys(weeklyData).length + 1}`,
            outbound: [],
            inbound: [],
            delivered: 0,
            failed: 0
          };
        }
        
        if (msg.direction === 'outbound') {
          weeklyData[weekKey].outbound.push(msg);
          if (msg.status === 'delivered') weeklyData[weekKey].delivered++;
          if (msg.status === 'failed') weeklyData[weekKey].failed++;
        } else {
          weeklyData[weekKey].inbound.push(msg);
        }
      });

      // Calculate rates for each week
      const performanceData = Object.values(weeklyData).map(week => {
        const totalSent = week.outbound.length;
        const totalReplies = week.inbound.length;
        const deliveryRate = totalSent > 0 ? ((week.delivered / totalSent) * 100).toFixed(1) : 0;
        const responseRate = totalSent > 0 ? ((totalReplies / totalSent) * 100).toFixed(1) : 0;
        const engagementRate = totalSent > 0 ? ((totalReplies / totalSent) * 100).toFixed(1) : 0;

        return {
          period: week.period,
          deliveryRate: parseFloat(deliveryRate),
          responseRate: parseFloat(responseRate),
          engagementRate: parseFloat(engagementRate)
        };
      });

      return { data: performanceData, error: null };
    } catch (err) {
      console.error('Error fetching performance trends:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Get compliance monitoring data
   */
  async getComplianceData(agentId, dateRange = '30d') {
    try {
      const now = new Date();
      let startDate = new Date();
      
      switch (dateRange) {
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        case 'all':
          startDate = new Date(0);
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }

      const { data: messages, error } = await supabase
        .from('messages')
        .select('id, direction, status, created_at, error_code, client_id')
        .eq('agent_id', agentId)
        .gte('created_at', startDate.toISOString());

      if (error) throw error;

      const outbound = messages?.filter(m => m.direction === 'outbound') || [];
      const delivered = outbound.filter(m => m.status === 'delivered').length;
      const failed = outbound.filter(m => m.status === 'failed').length;
      const totalSent = outbound.length;

      // Calculate metrics
      const deliverySuccess = totalSent > 0 ? ((delivered / totalSent) * 100).toFixed(1) : '100.0';
      const optOutRate = '0.0'; // Would need opt-out tracking
      const spamReports = '0.0'; // Would need spam report tracking
      
      // Calculate average response time (time between outbound and next inbound)
      let totalResponseTime = 0;
      let responseCount = 0;
      
      for (let i = 0; i < outbound.length; i++) {
        const outboundMsg = outbound[i];
        const nextInbound = messages?.find(m => 
          m.direction === 'inbound' && 
          new Date(m.created_at) > new Date(outboundMsg.created_at) &&
          m.client_id === outboundMsg.client_id
        );
        
        if (nextInbound) {
          const responseTime = (new Date(nextInbound.created_at) - new Date(outboundMsg.created_at)) / 1000 / 60; // minutes
          totalResponseTime += responseTime;
          responseCount++;
        }
      }
      
      const avgResponseTime = responseCount > 0 ? (totalResponseTime / responseCount).toFixed(1) : '0.0';

      // Get recent compliance events (failed deliveries)
      const recentFailures = outbound
        .filter(m => m.status === 'failed')
        .slice(0, 5)
        .map(m => ({
          id: m.id,
          title: 'Delivery Failure',
          description: `Message failed to deliver${m.error_code ? ` (Error: ${m.error_code})` : ''}`,
          timestamp: new Date(m.created_at).toLocaleString(),
          severity: 'warning',
          action: 'View Details'
        }));

      return {
        data: {
          overallScore: parseFloat(deliverySuccess),
          metrics: [
            {
              id: 1,
              name: 'Opt-out Rate',
              value: `${optOutRate}%`,
              status: parseFloat(optOutRate) < 5 ? 'excellent' : parseFloat(optOutRate) < 10 ? 'good' : 'warning',
              description: parseFloat(optOutRate) < 5 ? 'Below 5% threshold' : 'Above threshold'
            },
            {
              id: 2,
              name: 'Delivery Success',
              value: `${deliverySuccess}%`,
              status: parseFloat(deliverySuccess) >= 95 ? 'excellent' : parseFloat(deliverySuccess) >= 90 ? 'good' : 'warning',
              description: parseFloat(deliverySuccess) >= 95 ? 'Above 95% target' : 'Below target'
            },
            {
              id: 3,
              name: 'Response Time',
              value: `${avgResponseTime}min`,
              status: parseFloat(avgResponseTime) < 5 ? 'excellent' : parseFloat(avgResponseTime) < 15 ? 'good' : 'warning',
              description: 'Average client response time'
            },
            {
              id: 4,
              name: 'Spam Reports',
              value: `${spamReports}%`,
              status: parseFloat(spamReports) < 1 ? 'excellent' : 'warning',
              description: parseFloat(spamReports) < 1 ? 'Well below 1% limit' : 'Above limit'
            }
          ],
          events: recentFailures
        },
        error: null
      };
    } catch (err) {
      console.error('Error fetching compliance data:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Get campaign comparison data (based on touchpoint types if available)
   */
  async getCampaignComparison(agentId, dateRange = '30d') {
    try {
      // For now, return empty array - can be enhanced with touchpoint data
      return { data: [], error: null };
    } catch (err) {
      console.error('Error fetching campaign comparison:', err);
      return { data: null, error: err };
    }
  }

  /**
   * Get automated insights based on real data
   */
  async getAutomatedInsights(agentId, dateRange = '30d') {
    try {
      const now = new Date();
      let startDate = new Date();
      
      switch (dateRange) {
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        case 'all':
          startDate = new Date(0);
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }

      const { data: messages, error } = await supabase
        .from('messages')
        .select('direction, status, created_at, client_id')
        .eq('agent_id', agentId)
        .gte('created_at', startDate.toISOString());

      if (error) throw error;

      const insights = [];
      
      // Analyze reply rate trends
      const outbound = messages?.filter(m => m.direction === 'outbound') || [];
      const inbound = messages?.filter(m => m.direction === 'inbound') || [];
      const replyRate = outbound.length > 0 ? (inbound.length / outbound.length) * 100 : 0;

      if (replyRate < 20 && outbound.length > 10) {
        insights.push({
          id: 1,
          type: 'warning',
          priority: 'medium',
          title: 'Low Reply Rate Detected',
          description: `Reply rate is ${replyRate.toFixed(1)}%, below the 20% threshold`,
          timestamp: 'Just now',
          analysis: `Your messages are receiving fewer replies than expected. Consider personalizing messages or adjusting send times.`,
          recommendations: [
            'Personalize message content',
            'Optimize send times',
            'Review message frequency'
          ],
          actions: ['Review Messages', 'Adjust Strategy']
        });
      }

      // Check for delivery failures
      const failed = outbound.filter(m => m.status === 'failed').length;
      if (failed > 0 && outbound.length > 0) {
        const failureRate = (failed / outbound.length) * 100;
        if (failureRate > 5) {
          insights.push({
            id: 2,
            type: 'warning',
            priority: 'high',
            title: 'High Delivery Failure Rate',
            description: `${failureRate.toFixed(1)}% of messages failed to deliver`,
            timestamp: 'Just now',
            analysis: `A significant portion of your messages are failing to deliver. This may indicate invalid phone numbers or carrier issues.`,
            recommendations: [
              'Verify client phone numbers',
              'Check for invalid numbers',
              'Review carrier restrictions'
            ],
            actions: ['Update Contacts', 'Review Failures']
          });
        }
      }

      return { data: insights, error: null };
    } catch (err) {
      console.error('Error fetching insights:', err);
      return { data: null, error: err };
    }
  }
}

// Export singleton instance
export const twilioService = new TwilioService();
export default twilioService;

